import React from 'react';
import { Interp, css, toList } from '../../lib/render.jsx';
import StartMenu from './StartMenu.jsx';
import { Beker, Boek, Bubbel, Joystick, Kruis, Potlood } from '../Iconen.jsx';

/**
 * De taakbalk onderin.
 *
 * Links de startknop met jouw kop erop, daarnaast de programma's die draaien,
 * rechts het systeemvak met de chat, de bel en de klok. De vorm komt van Windows
 * XP; de knoppen erin zijn gewoon de knoppen van deze app.
 *
 * Twee soorten knoppen, en de plek zegt welke: een programma staat links en heeft
 * zijn naam ernaast (Tekenen, en de spelletjes zodra je die opent), het systeemvak
 * staat rechts en heeft alleen tekentjes. De chat hoort bij die tweede groep. Hij
 * stond eerst met zijn naam tussen de programma's, maar je gaat niet naar de chat
 * toe zoals je naar een programma gaat: hij kijkt mee, net als de bel.
 *
 * Op een telefoon vervallen de namen; daar past alleen het tekentje.
 *
 * Het uitklapmenu van de bel hangt hieraan vast en klapt naar boven open; zie
 * notifDropdownStyle in logic/render-vals.js.
 */
/**
 * Welk tekentje bij welk programma hoort.
 *
 * De vier nieuwe staan in Iconen.jsx, want ze komen ook op het bureaublad terug; de
 * hoorn van de Krabbellijn en het kopje van de Krabbelgotchi staan alleen hier, en
 * een component voor één plek is een omweg (zie de kop van Iconen.jsx).
 */
function ProgIcoon({ id, maat, dikte }) {
  if (id === 'studio') return <Potlood maat={maat} />;
  // Een kader voor de krabbel waar je naar kijkt: het is een plaatje, geen potlood.
  if (id === 'arcade') return <Joystick maat={maat} />;
  if (id === 'activity') return <Beker maat={maat} />;
  if (id === 'gastenboek') return <Boek maat={maat} />;
  if (id === 'beest') {
    // Een kopje met twee oortjes: het silhouet van het beestje zelf.
    return (
      <svg width={maat} height={maat} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={dikte} strokeLinecap="round" strokeLinejoin="round">
        <path d="M7.5 6.5V3.4" />
        <path d="M16.5 6.5V3.4" />
        <rect x="4.2" y="6.5" width="15.6" height="14.2" rx="5" />
        <path d="M10 12.4v2.2" />
        <path d="M14 12.4v2.2" />
      </svg>
    );
  }
  /* Een telefoonhoorn, want dat is de Krabbellijn: doorfluisteren. Hier stond een
     huppelende lijn met een pijltje, en daarvoor een zigzag. Beide klopten met de
     naam maar niet met het formaat: op twintig pixels met een lijn van 2,6 loopt zo'n
     bochtje dicht en blijft er een worm over. Deze is één doorlopende vorm en houdt
     daarom zijn silhouet, ook klein. */
  return (
    <svg width={maat} height={maat} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={dikte} strokeLinecap="round" strokeLinejoin="round">
      <path d="M6.3 3.6c.9 0 1.5.5 1.8 1.3l.8 2.2c.2.7 0 1.4-.6 1.7l-1.2.8c1 1.9 2.4 3.3 4.3 4.3l.8-1.2c.4-.5 1-.7 1.7-.5l2.2.8c.8.3 1.3 1 1.3 1.8v2c0 1.1-1 2-2 1.9C9.1 19.3 4.7 15 3.7 6.9c-.1-1.1.8-2 1.9-2z" />
    </svg>
  );
}

export default function Taskbar({ v }) {
  return (
    <>
      <StartMenu v={v} />
      <div style={css(v.taskbarStyle)}>
        <button onClick={v.toggleStart} className="gp-btn gp-hoek" title={v.startTitel} aria-expanded={v.startOpen} style={css(v.startBtnStyle)}>
          {v.heeftAvatar
            ? <img alt="" src={v.myAvatar} style={css(v.startAvatarStyle)} />
            : <span style={css(v.startLeegStyle)}>?</span>}
          {v.showStartNaam ? (
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              <Interp v={v.startNaam} />
            </span>
          ) : null}
        </button>

        {/* De programma's die draaien: links, meteen naast de startknop, met hun
            naam ernaast zoals in de balk van XP. Zie progStyle in logic/desktop.js.
            Ze stonden rechts in het systeemvak en duwden de bel en de klok dan elke
            keer een stuk naar links zodra er een venster bij kwam. */}
        {/* De programma's die draaien: links, meteen naast de startknop, met hun
            naam ernaast zoals in de balk van XP. De lijst komt uit progLijst in
            logic/desktop.js; hier staat alleen welk tekentje bij welk programma hoort.
            Er stonden drie knoppen met de hand uitgeschreven; bij zeven is dat zeven
            keer dezelfde zes regels. */}
        <div style={css(v.progStyle)}>
          {toList(v.progKnoppen).map((k) => (
            <div key={k?.id} style={{ position: 'relative', flex: '0 0 auto' }}>
              <button onClick={k?.onClick} className="gp-btn" title={k?.titel} aria-label={k?.titel} style={css(k?.style)}>
                <ProgIcoon id={k?.id} maat={v.trayIcon} dikte={v.trayStroke} />
                {k?.naam ? <span style={css(v.progNaamStyle)}>{k.naam}</span> : null}
                {k?.stip ? <span style={{ position: 'absolute', top: '-6px', right: '-6px', width: '15px', height: '15px', borderRadius: '50%', background: 'var(--roze)', border: '3px solid var(--ink)' }} /> : null}
              </button>
            </div>
          ))}
        </div>

        {/* Het systeemvak: het chatvenster, de bel, en hoe laat het is.
            Het modgereedschap stond hier ook; dat woont nu in het startmenu. */}
        <div style={css(v.trayStyle)}>
          <button onClick={v.openChatWindow} className="gp-btn" title="Open het chatvenster" style={css(v.chatWinBtnStyle)}>
            <Bubbel maat={v.trayIcon} />
            {v.showBarBadge ? <span style={css(v.barBadgeStyle)}><Interp v={v.barBadge} /></span> : null}
          </button>

          <div style={{ position: 'relative', flex: '0 0 auto' }}>
            <button onClick={v.toggleNotif} className="gp-btn" title="Updates" style={css(v.trayBtnStyle)}>
              <svg width={v.trayIcon} height={v.trayIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={v.trayStroke} strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
                <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
              </svg>
              {v.showNotifDot ? <span style={{ position: 'absolute', top: '-6px', right: '-6px', width: '15px', height: '15px', borderRadius: '50%', background: 'var(--roze)', border: '3px solid var(--ink)' }} /> : null}
            </button>

            {v.notifOpen ? (
              <div style={css(v.notifDropdownStyle)}>
                <div style={css(v.notifHeaderStyle)}>
                  <div style={css(v.notifTitleStyle)}>Nieuws</div>
                  {/* Hetzelfde kruisje als in het startmenu: twintig groot, met een raakvlak
                      van 44 dat de kop niet verschuift. Het was 16 groot in een vakje van 24. */}
                  <button onClick={v.toggleNotif} title="Sluiten" style={css(v.startSluitStyle)}>
                    <Kruis maat={v.notifCloseIcon} />
                  </button>
                </div>
                <div style={css(v.notifBodyStyle)}>
                  <div style={css(v.notifListStyle)}>
                    {toList(v.notifFeed).map((n, $index) => (
                      <React.Fragment key={$index}>
                        <button onClick={n?.onClick} title={n?.title} style={css(n?.rijStyle)} className="gp-h1">
                          <span style={css(v.notifAvatarWrapStyle)}>
                            <img alt="" src={n?.avatarUrl} style={css(v.notifAvatarStyle)} />
                            <span style={css(v.notifBadgeStyle)}><Interp v={n?.icon} /></span>
                          </span>
                          <span style={css(v.notifTekstVakStyle)}>
                            <span style={css(n?.textStyle)}><Interp v={n?.text} /></span>
                            <span style={css(v.notifWhenStyle)}><Interp v={n?.when} /></span>
                          </span>
                        </button>
                      </React.Fragment>
                    ))}
                    {v.showNotifMore ? (
                      <button onClick={v.toggleNotifMore} className="gp-btn" style={css(v.notifMoreBtnStyle)}>
                        <Interp v={v.notifMoreLabel} />
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          {v.showKlok ? (
            <div style={css(v.klokStyle)} title={v.klokDatum}>
              <span style={css(v.klokTijdStyle)}><Interp v={v.klokTijd} /></span>
              <span style={css(v.klokDatumStyle)}><Interp v={v.klokDatum} /></span>
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}
